import type { MinimapFiles } from "@novorender/api";

import { log } from "utils/log";

const l = log.miniMap.tiled;

/** Subfolder tiled minimap files live under, inside the SDK's OPFS scene directory (matches `offlineStore.ts`). */
const MINIMAP_DIR_NAME = "minimap";

/**
 * Paths per removal. A scene whose sheets were all re-mapped can strand tens
 * of thousands of files (a single reference tile set is ~1,700), and each
 * removal crosses into the SDK's IO worker — handing it one array that size
 * is a multi-MB message on exactly the devices this feature exists to protect.
 */
const PRUNE_BATCH_SIZE = 1_000;

/**
 * What a prune reads and removes through: the files stored under `minimap/`,
 * and a removal of some of them.
 * @remarks
 * Two sources, by the scene's mode. A full sync's directory changes only
 * inside a sync, so there it is the folder the SDK hands the sync's
 * `syncMinimap` callback ({@link minimapSyncPruneStore}). An incremental
 * scene's is the scene directory itself ({@link incrementalScenePruneStore}).
 */
export type MinimapPruneStore = {
    list(): Promise<Iterable<{ readonly name: string }>>;
    remove(names: string[]): Promise<void>;
};

/** The slice of `OfflineScene` an incremental scene's prune needs. */
export type MinimapPruneScene = {
    readonly dir: {
        getJournalEntries(): Promise<IterableIterator<{ name: string; size: number }>>;
        deleteFiles(names: Iterable<string>): Promise<void>;
    };
    reportOfflineFilesRemoved(filenames: string[]): void;
};

/**
 * An incremental scene's directory, for the prune on scene load. Removals are
 * reported to the eviction loop, which would otherwise keep counting them
 * against the budget. A full sync's directory refuses these removals: prune
 * it inside the sync instead.
 *
 * When the SDK couldn't remove some of the files -- held open elsewhere, say --
 * it removes the rest and rejects naming the ones still there. The rest are
 * reported all the same, and the rejection is passed on.
 */
export function incrementalScenePruneStore(scene: MinimapPruneScene): MinimapPruneStore {
    return {
        list: () => scene.dir.getJournalEntries(),
        remove: async (names) => {
            try {
                await scene.dir.deleteFiles(names);
            } catch (e) {
                const notRemoved = filesNotRemoved(e);
                const removed = notRemoved ? names.filter((name) => !notRemoved.has(name)) : [];
                if (removed.length > 0) {
                    scene.reportOfflineFilesRemoved(removed);
                }
                throw e;
            }
            scene.reportOfflineFilesRemoved(names);
        },
    };
}

/**
 * The files `deleteFiles` says it couldn't remove, having removed the rest: an `Error` named
 * `"FilesNotRemoved"`, with their names in `names`. `undefined` for any other failure, which
 * reports nothing removed: either nothing was, what went is unknown, or the files went but
 * stay journaled (the journal held elsewhere), so the eviction loop's next scan counts them
 * anyway.
 */
function filesNotRemoved(error: unknown): ReadonlySet<string> | undefined {
    if (!(error instanceof Error) || error.name !== "FilesNotRemoved") {
        return undefined;
    }
    const names: unknown = (error as Error & { names?: unknown }).names;
    return Array.isArray(names) ? new Set(names.filter((name): name is string => typeof name === "string")) : undefined;
}

/** The folder a full sync hands its `syncMinimap` callback, for the prune at the end of the download. */
export function minimapSyncPruneStore(files: Pick<MinimapFiles, "list" | "remove">): MinimapPruneStore {
    return {
        list: () => files.list(),
        remove: (names) => files.remove(names),
    };
}

/** The `{fid}` a journaled `minimap/{fid}/...` path belongs to, or `undefined` for anything else. */
function fileInstanceIdFromMinimapPath(name: string): string | undefined {
    const segments = name.split("/");
    return segments.length >= 2 && segments[0] === MINIMAP_DIR_NAME ? segments[1] : undefined;
}

function throwIfAborted(abortSignal: AbortSignal | undefined): void {
    if (abortSignal?.aborted) {
        throw (abortSignal.reason as Error | undefined) ?? new Error("Aborted");
    }
}

/**
 * Remove every `minimap/{fid}/...` tree whose `fid` is not in `currentFileInstanceIds`: a
 * sheet re-mapped (which mints a new `fid`) or removed from the scene. Neither eviction nor a
 * sync knows which `fid`s are current, so without this an incremental scene's stale tiles
 * crowd out its geometry cache, and a full sync's directory, pruned inside the sync, grows.
 *
 * @param currentFileInstanceIds The scene's current drawing set, or `undefined` when the
 * caller couldn't determine it, which prunes nothing rather than everything.
 * @remarks
 * An abort propagates, checked before listing, before removing and per batch, so a pause
 * doesn't read as "nothing was stale".
 * @throws What a removal rejects with, the later batches left for the next
 * prune -- but for files the store couldn't remove while removing the rest
 * (an `Error` named `"FilesNotRemoved"`): the later batches go on, and the
 * prune then rejects once in the same shape, naming every file left.
 */
export async function pruneStaleMinimapFiles({
    store,
    currentFileInstanceIds,
    abortSignal,
}: {
    store: MinimapPruneStore;
    currentFileInstanceIds: readonly string[] | undefined;
    abortSignal?: AbortSignal;
}): Promise<void> {
    if (currentFileInstanceIds === undefined) {
        return;
    }
    throwIfAborted(abortSignal);

    const currentSet = new Set(currentFileInstanceIds);
    const stalePathsByFid = new Map<string, string[]>();
    for (const entry of await store.list()) {
        const fid = fileInstanceIdFromMinimapPath(entry.name);
        if (!fid || currentSet.has(fid)) {
            continue;
        }
        const paths = stalePathsByFid.get(fid);
        if (paths) {
            paths.push(entry.name);
        } else {
            stalePathsByFid.set(fid, [entry.name]);
        }
    }

    if (stalePathsByFid.size === 0) {
        return;
    }
    throwIfAborted(abortSignal);

    const stalePaths = [...stalePathsByFid.values()].flat();
    l.debug("Pruning stale minimap tile sets:", [...stalePathsByFid.keys()]);
    const left: string[] = [];
    for (let i = 0; i < stalePaths.length; i += PRUNE_BATCH_SIZE) {
        throwIfAborted(abortSignal);
        try {
            await store.remove(stalePaths.slice(i, i + PRUNE_BATCH_SIZE));
        } catch (e) {
            const notRemoved = filesNotRemoved(e);
            if (!notRemoved) {
                throw e;
            }
            left.push(...notRemoved);
        }
    }
    if (left.length > 0) {
        throw Object.assign(new Error(`Stale mini map files not removed: ${left.length}`), {
            name: "FilesNotRemoved",
            names: left,
        });
    }
}
